import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { TrendingUp, BarChart3, PieChart } from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface ChartRendererProps {
  type: string;
  data: Record<string, any>[];
  title: string;
  className?: string;
}

export const ChartRenderer: React.FC<ChartRendererProps> = ({
  type,
  data,
  title,
  className,
}) => {
  if (!data || data.length === 0) {
    return (
      <Card className={className}>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-secondary-100 rounded-2xl flex items-center justify-center mb-4">
              <BarChart3 className="w-8 h-8 text-secondary-400" />
            </div>
            <p className="text-secondary-500 font-medium">No data available for visualization</p>
            <p className="text-sm text-secondary-400 mt-1">Try adjusting your query parameters</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Improved logic to extract keys for x and y axes
  const keys = Object.keys(data[0]);
  
  // For time series data, prioritize the 'period' field
  let xKey: string;
  let yKey: string;
  
  if (type.toLowerCase() === 'line' && keys.includes('period')) {
    // For line charts with period field, use period as x-axis
    xKey = 'period';
    yKey = keys.find(k => k !== 'period' && ['total', 'value', 'y', 'count', 'total_sales', 'sales', 'amount'].some(metric => k.toLowerCase().includes(metric))) || 
           keys.find(k => k !== 'period' && typeof data[0][k] === 'number') ||
           keys.find(k => k !== 'period') || keys[0];
  } else {
    // For other chart types, use the original logic
    xKey = keys.find(k => !['total', 'value', 'y', 'count', 'total_sales', 'sales', 'amount'].includes(k.toLowerCase())) || keys[0];
    yKey = keys.find(k => ['total', 'value', 'y', 'count', 'total_sales', 'sales', 'amount'].includes(k.toLowerCase())) || 
           keys.find(k => typeof data[0][k] === 'number') ||
           keys[1];
  }

  // Sort data by period for line charts to ensure proper time sequence
  let sortedData = [...data];
  if (type.toLowerCase() === 'line' && xKey === 'period') {
    sortedData.sort((a, b) => {
      const periodA = String(a[xKey] || '');
      const periodB = String(b[xKey] || '');
      return periodA.localeCompare(periodB);
    });
  }

  const chartData = {
    labels: sortedData.map((d: any) => String(d[xKey])),
    datasets: [
      {
        label: title,
        data: sortedData.map((d: any) => Number(d[yKey]) || 0),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(6, 182, 212, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(168, 85, 247, 0.8)',
          'rgba(251, 146, 60, 0.8)',
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(139, 92, 246, 1)',
          'rgba(236, 72, 153, 1)',
          'rgba(6, 182, 212, 1)',
          'rgba(34, 197, 94, 1)',
          'rgba(168, 85, 247, 1)',
          'rgba(251, 146, 60, 1)',
        ],
        borderWidth: 2,
        borderRadius: type === 'bar' ? 6 : 0,
        tension: type === 'line' ? 0.4 : 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: 500,
          },
          color: '#374151',
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(75, 85, 99, 0.2)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 16,
        titleFont: {
          size: 14,
          weight: 600,
        },
        bodyFont: {
          size: 13,
          weight: 500,
        },
        displayColors: true,
        usePointStyle: true,
      },
    },
    scales: type !== 'pie' && type !== 'doughnut' ? {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            weight: 500,
          },
          color: '#6B7280',
        },
        border: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            weight: 500,
          },
          color: '#6B7280',
          padding: 8,
        },
        border: {
          display: false,
        },
      },
    } : undefined,
    elements: {
      point: {
        radius: 6,
        hoverRadius: 8,
        backgroundColor: 'white',
        borderWidth: 3,
      },
      line: {
        borderWidth: 3,
      },
    },
  };

  const getChartIcon = () => {
    switch (type.toLowerCase()) {
      case 'line':
        return <TrendingUp className="w-5 h-5 text-primary-600" />;
      case 'pie':
      case 'doughnut':
        return <PieChart className="w-5 h-5 text-primary-600" />;
      default:
        return <BarChart3 className="w-5 h-5 text-primary-600" />;
    }
  };

  const renderChart = () => {
    switch (type.toLowerCase()) {
      case 'bar':
        return <Bar data={chartData} options={options} />;
      case 'line':
        return <Line data={chartData} options={options} />;
      case 'pie':
        return <Pie data={chartData} options={options} />;
      case 'doughnut':
        return <Doughnut data={chartData} options={options} />;
      default:
        return <Bar data={chartData} options={options} />;
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center">
            {getChartIcon()}
          </div>
          <div>
            <h3 className="text-lg font-bold text-secondary-900">{title}</h3>
            <p className="text-sm text-secondary-600 font-medium">
              {data.length} data point{data.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-80 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary-50/30 to-primary-50/30 rounded-lg -z-10"></div>
          {renderChart()}
        </div>
      </CardContent>
    </Card>
  );
};